import PropTypes from 'prop-types';
import {
    Recommended,
    Website,
    Ticketing,
    Productivity,
    MarketingPromotion,
    CrmAppMarketplace,
    Email,
    Reporting,
    Popular,
} from '../svgs/index';

import { gettext } from '@eb/i18n';

export const MAX_APPS_TO_DISPLAY = 15;

export const BASE_URL = '/';
export const BASE_APPS_URL = '/apps/';

export const RECOMMENDED = 'recommended';
export const POPULAR = 'popular';

export const CATEGORIES_INFO_MAP = {
    crm: {
        name: gettext('CRM'),
        illustration: CrmAppMarketplace,
        description: gettext('Apps to generate and manage new leads'),
        slug: 'crm',
    },
    email: {
        name: gettext('Email'),
        illustration: Email,
        description: gettext('Apps to elevate your email marketing'),
        slug: 'email',
    },
    reporting: {
        name: gettext('Reporting'),
        illustration: Reporting,
        description: gettext('Apps that do math'),
        slug: 'reporting',
    },
    'marketing-and-promotion': {
        name: gettext('Marketing & Promotion'),
        illustration: MarketingPromotion,
        description: gettext('Apps to reach more people in more places'),
        slug: 'marketing-and-promotion',
    },
    productivity: {
        name: gettext('Productivity'),
        illustration: Productivity,
        description: gettext('Apps to streamline, automate, and get organized'),
        slug: 'productivity',
    },
    'tickets-and-onsite': {
        name: gettext('Tickets & Onsite'),
        illustration: Ticketing,
        description: gettext(
            'Apps to print tickets & badges and make your day-of shine',
        ),
        slug: 'tickets-and-onsite',
    },
    website: {
        name: gettext('Website'),
        illustration: Website,
        description: gettext('Apps to create amazing web experiences'),
        slug: 'website',
    },
    popular: {
        name: gettext('Popular'),
        illustration: Popular,
        description: gettext('Top apps for event creators'),
    },
    recommended: {
        name: gettext('Recommended'),
        illustration: Recommended,
        //eslint-disable-next-line quotes
        description: gettext("Apps we know you'll love"),
    },
};

export const CURRENT_CATEGORIES_TO_NEW_MAP = {
    crm: 'crm',
    'email-marketing': 'email',
    'promotion-and-advertising': 'marketing-and-promotion',
    'marketing-tools': 'marketing-and-promotion',
    websites: 'website',
    'mobile-apps': 'tickets-and-onsite',
    'multi-session-management': 'productivity',
    'onsite-opertaions': 'tickets-and-onsite',
    other: 'productivity',
    fundraising: 'marketing-and-promotion',
    'finance-and-reporting': 'reporting',
    'printed-tickets': 'tickets-and-onsite',
    'app-connectors': 'productivity',
    'event-management-software': 'productivity',
    'eventbrite-dashboard': 'productivity',
};

export const CURRENT_APPS_TO_NEW_MAP = {
    toneden: 'paid-social-ads',
};

export const APP_DETAILS_SHAPE = PropTypes.shape({
    isInstalled: PropTypes.bool,
    _type: PropTypes.string,
});

export const APP_EXTENSION_SHAPE = PropTypes.shape({
    clientId: PropTypes.string,
    extensionUrl: PropTypes.string,
    requiresOauth: PropTypes.bool,
    slug: PropTypes.string,
});

export const RESOURCE_LINKS_SHAPE = PropTypes.arrayOf(
    PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
    }),
);

export const SCREENSHOTS_SHAPE = PropTypes.arrayOf(
    PropTypes.shape({
        id: PropTypes.string,
        imageUrl: PropTypes.string,
        sortOrder: PropTypes.string,
    }),
);

export const APP_LOCALIZATION_SHAPE = PropTypes.shape({
    contactInfo: PropTypes.shape({
        supportEmail: PropTypes.string,
    }),
    description: PropTypes.string,
    getStartedUrl: PropTypes.string,
    id: PropTypes.string,
    language: PropTypes.string,
    resourceLinks: RESOURCE_LINKS_SHAPE,
    screenshots: SCREENSHOTS_SHAPE,
    shortDescription: PropTypes.string,
    supportDescription: PropTypes.string,
});

export const RATINGS_SUMMARY_SHAPE = PropTypes.shape({
    totalReviews: PropTypes.number,
    rating: PropTypes.number,
    stars: PropTypes.shape({
        one: PropTypes.number,
        two: PropTypes.number,
        three: PropTypes.number,
        four: PropTypes.number,
        five: PropTypes.number,
    }),
});

export const RATINGS_SHAPE = PropTypes.shape({
    rating: PropTypes.number,
    created: PropTypes.string,
    locale: PropTypes.string,
    reviewText: PropTypes.string,
    reviewTitle: PropTypes.string,
    id: PropTypes.string,
});

export const RATINGS_SUMMARY_STARS_SHAPE = PropTypes.shape({
    one: PropTypes.number,
    two: PropTypes.number,
    three: PropTypes.number,
    four: PropTypes.number,
    five: PropTypes.number,
});

export const REVIEW_REPLY_SHAPE = PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    created: PropTypes.string,
});

export const DEFAULT_LANGUAGE_TO_LOCALE = {
    'de-de': 'de_DE',
    'en-au': 'en_AU',
    'en-ca': 'en_CA',
    'en-gb': 'en_GB',
    'en-us': 'en_US',
    'es-es': 'es_ES',
    'fr-ca': 'fr_CA',
    'fr-fr': 'fr_FR',
    'it-it': 'it_IT',
    'nl-nl': 'nl_NL',
    'pt-pt': 'pt_PT',
    'pt-br': 'pt_BR',
    'es-ar': 'es_AR',
    'en-se': 'en_SE',
};

export const REVIEW_SUBMIT_ERRORS = {
    APP_ALREADY_RATED: 'APP_ALREADY_RATED',
    APP_NOT_INSTALLED: 'APP_NOT_INSTALLED',
    UNKNOWN: 'UNKNOWN',
    ARGUMENTS_ERROR: 'ARGUMENTS_ERROR',
};

export const REVIEW_SUBMIT_ERROR_PROP_TYPE = [
    REVIEW_SUBMIT_ERRORS.APP_ALREADY_RATED,
    REVIEW_SUBMIT_ERRORS.APP_NOT_INSTALLED,
    REVIEW_SUBMIT_ERRORS.UNKNOWN,
];

export const HALF_STAR_SIZE_PROP_TYPE = [
    'xsmall',
    'small',
    'medium',
    'large',
    'xlarge',
];

export const MINIMUM_NUMBER_OF_APP_RATINGS = 5;

export const APP_RATINGS_PER_PAGE = 3;

export const APP_RATING_SORT_OPTIONS = {
    MOST_RECENT: 'created_desc',
    TOP_RATED: 'rating_desc',
};

export const WRITE_REVIEW_FORM_NAME = 'writeReview';

export const SMALL_WIDE = 660;
export const LARGE_WIDE = 1272;

export const LARGE = 'LARGE';
export const MEDIUM = 'MEDIUM';
export const SMALL = 'SMALL';
